import { useT } from "@/i18n/LanguageContext";
import { cardPublicUrl, CATEGORY_LABEL_KEYS } from "@/data/trainingCards";
import { Maximize2 } from "lucide-react";

/**
 * Grid tile rendering a card preview. Clicking opens the lightbox modal.
 *
 * The actual PNG is loaded directly (no resizing on the client) and rendered
 * inside a square frame with subtle gold border + hover lift.
 */
export default function CardTile({ card, onOpen }) {
  const { t, lang } = useT();
  const theme = card.theme[lang] || card.theme.en;
  const categoryLabel = t(CATEGORY_LABEL_KEYS[card.category] || "cards.cat.virtue");

  return (
    <button
      type="button"
      onClick={() => onOpen(card)}
      data-testid={`card-tile-${card.slug}`}
      className="group relative block w-full text-left focus:outline-none focus:ring-1 focus:ring-gold/60"
    >
      <div
        className="relative aspect-square overflow-hidden border border-gold/15 group-hover:border-gold/45 transition-colors duration-500 shadow-[0_15px_45px_-15px_rgba(0,0,0,0.7)] group-hover:shadow-[0_25px_70px_-15px_rgba(212,175,55,0.18)]"
        style={{ backgroundColor: card.bgColor || "#070605" }}
      >
        <img
          src={cardPublicUrl(card)}
          alt={`${theme}, ${card.attribution}`}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
        />
        {/* hover overlay with expand hint */}
        <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/30 transition-colors duration-500 pointer-events-none" />
        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-black/60 border border-gold/60 backdrop-blur-sm">
            <Maximize2 size={14} strokeWidth={1.5} className="text-gold" />
          </span>
        </div>
      </div>
      <div className="mt-4 flex items-baseline justify-between gap-3">
        <div>
          <p className="text-[10px] tracking-[0.28em] uppercase text-gold/85">
            {categoryLabel} · {theme}
          </p>
          <p className="mt-1 font-serif text-bone text-base md:text-lg tracking-tight">
            {card.attribution}
          </p>
        </div>
      </div>
    </button>
  );
}
